import type { HelpArticle } from "../../types";
import { MAPLE_PROJECT } from "../../../calculations/calcPreviewFixtures";

export const designConditionsArticle: HelpArticle = {
  id: "design-conditions",
  title: "Wood design conditions",
  description:
    "Set the method, service environment, serviceability criteria, repetitive-member election, and expert reference-value overrides for a wood member.",
  tags: ["wood", "design method", "service", "temperature", "Kcr", "deflection", "factors"],
  lastUpdated: "July 20, 2026",
  published: false,
  blocks: [
    {
      kind: "lead",
      text: "Card C contains choices that change the calculation without changing the section or load rows. They are persisted inputs. The result ledger is the evidence of what the engine actually applied; the portal does not calculate adjusted design values in the browser.",
    },
    { kind: "where", path: ["Calculations", MAPLE_PROJECT.name, "FJ-1", "C. Design conditions"] },

    { kind: "heading", level: 2, text: "Choose the effective design method", id: "method" },
    {
      kind: "paragraph",
      text: "**Project default** leaves the member's method inherited. **ASD override** and **LRFD override** persist an explicit sheet choice. The Card C design-basis strip and the returned result identify the effective method; a project-default change recomputes inherited member sheets but does not replace an explicit override.",
    },
    {
      kind: "paragraph",
      text: "Method selection changes the calculation contract. Do not compare ASD and LRFD DCRs as if only the badge changed: combinations, resistance or duration factors, and the returned trace can differ.",
    },

    { kind: "heading", level: 2, text: "Declare service and temperature", id: "service" },
    {
      kind: "paragraph",
      text: "Service condition and temperature are engine inputs. Dry or wet service determines which moisture-service treatment applies; the temperature selection supplies the declared temperature basis. The result's factor ledger shows the determinations produced from those inputs.",
    },
    {
      kind: "shot",
      shotId: "wood-conditions-card",
      caption: "FJ-1 Card C keeps method, environment, and serviceability criteria in the same persisted input group.",
    },
    {
      kind: "callout",
      variant: "warning",
      title: "Inputs are not proof that a factor applied",
      body: "Use the returned factor ledger and adjusted values to confirm the engine's determination. A control selection alone is not the calculation record.",
    },

    { kind: "heading", level: 2, text: "Set serviceability criteria explicitly", id: "serviceability" },
    {
      kind: "paragraph",
      text: "Live-load, total-load, long-term, and optional absolute deflection limits are sheet inputs. The interface records the chosen criteria; it does not declare them universally appropriate. Cantilever ratio limits use twice the overhang projection, as the Card C note states.",
    },
    {
      kind: "paragraph",
      text: "Long-term deflection is enabled when its ratio is positive. The engine adds the creep increment `(Kcr - 1) × dead-load deflection` to the immediate deflection. When that check is active, the engine rejects Kcr below 1.5 for dry service or below 2.0 for wet service. The number input's minimum follows the same service selection, but server validation remains authoritative.",
    },
    {
      kind: "callout",
      variant: "note",
      title: "Kcr is not a browser-only hint",
      body: "The UI minimum helps entry; the member engine independently enforces the service-specific minimum when long-term deflection is checked.",
    },

    { kind: "heading", level: 2, text: "Elect repetitive-member behavior", id: "repetitive" },
    {
      kind: "paragraph",
      text: "The repetitive-member control is an engineering election, not a conclusion inferred solely from spacing. Set it only when the member arrangement satisfies the project basis. The returned factor ledger shows whether a repetitive-member determination was included for the calculated record.",
    },
    {
      kind: "shot",
      shotId: "wood-conditions-ledger",
      caption: "The saved result ledger, not Card C alone, records the factors and adjusted values the engine returned.",
    },

    { kind: "heading", level: 2, text: "Use reference-value overrides as an expert path", id: "overrides" },
    {
      kind: "paragraph",
      text: "Ordinary member work uses the selected database record. **Advanced · reference values** exposes explicit overrides for cases where the engineer has a defensible alternate basis. Leaving an override blank preserves the database path; entering one changes the calculation input and should be supported by project documentation.",
    },
    {
      kind: "shot",
      shotId: "wood-conditions-overrides",
      caption: "Advanced reference-value fields are separated from the normal database-backed workflow and stay collapsed by default.",
    },
    {
      kind: "callout",
      variant: "warning",
      title: "An override transfers responsibility",
      body: "An entered reference value is not validated as a catalog fact merely because the sheet computes. Record its source and review the returned reference and warnings before relying on the result.",
    },

    { kind: "related", topics: ["member-sheets", "member-picker", "sheet-lifecycle", "your-first-member"] },
  ],
};
